import { InitOptions } from '@segment/analytics-next';
import toLower from 'lodash/toLower';

import {
  Tracking,
  TrackingCtaElement,
  TrackingEventCategory,
  TrackingEventName,
  TrackingEventType,
  TrackingModule,
} from '@/lib/types';

import { SectionProps } from '@/components/section';

import analytics from '@/analytics';
import { PageMetaProps } from '@/context/global-context';

/////////////////////////////////////////////////////////////////////////////////

const TrackingEventNameMap: Record<TrackingEventName, string> = {
  LOADED_A_PAGE: 'Loaded a Page',
  MODULE_VIEWED: 'Module Viewed',
  VISITOR_PAGE_CTA_CLICKED: 'Visitor Page CTA Clicked',
  LANDING_PAGE_CTA_CLICKED: 'Landing Page CTA Clicked',
  ACCORDION_TOGGLE: 'Landing Page Accordion Opened',
  HOME_PAGE_CTA_CLICKED: 'Home Page CTA Clicked',
  CTA_CLICKED: 'CTA Clicked',
  PROSPECT_FORM_SUBMIT_BUTTON_CLICKED: 'Prospect Form Submit Button Clicked',
};

export const getClickTrackingEventNameByPage = (page: string) => {
  if (toLower(page) === 'home') return TrackingEventName.HomePageCtaClicked;
  else return TrackingEventName.CtaClicked;
};
/////////////////////////////////////////////////////////////////////////////////

const TrackingModuleMap: Record<TrackingModule, string> = {
  GENERIC: 'Generic',
  JOBS_BY_STATE: 'Jobs by State',
  REGISTER: 'Register',
  RELATED_CONTENT: 'Related Content',
  START_YOUR_NOMAD_ADVENTURE: 'Start your Nomad Adventure',
  TESTIMONIAL: 'Testimonial',
  FAQ: 'Faq',
};

/////////////////////////////////////////////////////////////////////////////////

const TrackingEventCategoryMap: Record<TrackingEventCategory, string> = {
  JOBS_BY_STATE_COMPONENT: 'Jobs by State Component',
  MODULE_VIEWED: 'Module Viewed',
  PAGE_LOAD: 'Page Load',
  REGISTER_COMPONENT: 'Register Component',
  RELATED_CONTENT_COMPONENT: 'Related Content Component',
  SEARCH_COMPONENT: 'Search Component',
  FAQ_COMPONENT: 'FAQ Component',
};

/////////////////////////////////////////////////////////////////////////////////

const TrackingEventTypeMap: Record<TrackingEventType, string> = {
  MODULE_IN_VIEW: 'Module Viewed',
  CLICK: 'CTA Clicked',
  PUBLIC_JOB_SEARCH: 'Public Job Search',
  REGISTER: 'Register',
};

/////////////////////////////////////////////////////////////////////////////////

interface TrackProps {
  pageMeta: PageMetaProps;
  name: TrackingEventName;
  category?: TrackingEventCategory;
  module?: TrackingModule;
  properties?: Record<string, unknown> & {
    module?: string | undefined;
    category?: string | undefined;
  };
  options?: InitOptions;
  callback?: () => void;
}
export const track = (props: TrackProps) => {
  try {
    const event = TrackingEventNameMap[props.name];
    const properties = {
      ...props.properties,
      title: document.title,
      page_title: document.title,
      referrer: document.referrer,
      path: window.location.pathname,
      url: window.location.href,
    };
    if (props.module) properties.module = TrackingModuleMap[props.module];
    if (props.category)
      properties.category = TrackingEventCategoryMap[props.category];
    analytics().track(event, properties);
  } catch (error) {
    // TODO: Implement Error Logging
  }
};

interface ModuleViewedTrackingProps {
  pageMeta: PageMetaProps;
  trackingValues?: Tracking;
  index?: number;
  sectionProps?: SectionProps;
  options?: InitOptions;
  callback?: () => void;
}

export const trackModuleViewed = (props: ModuleViewedTrackingProps) => {
  if (
    props.pageMeta.templateSetting?.enableSegmentTracking &&
    props.trackingValues &&
    props.trackingValues.trackingType
  ) {
    try {
      const event = TrackingEventTypeMap[props.trackingValues.trackingType];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const properties: any = {
        title: document.title,
        page_title: document.title,
        referrer: document.referrer,
        path: window.location.pathname,
        url: window.location.href,
        ...props.trackingValues.additionalTrackingParameters,
      };

      properties.module_viewed =
        props.trackingValues.moduleViewed || props.sectionProps?.title;
      properties.position_number =
        props.trackingValues.positionNumber || props.index;

      if (props.trackingValues.ctaCopy)
        properties.cta_copy = props.trackingValues.ctaCopy;

      analytics().track(event, properties);
    } catch (error) {
      // TODO: Implement Error Logging
    }
  }
};

export type ClickTrackingValues = {
  clickTrachingEnabled?: boolean;
  trackingEventName?: TrackingEventName;
  ctaType?: string;
  ctaElement?: TrackingCtaElement;
  moduleName?: string;
};
interface ClickTrackingProps {
  pageMeta: PageMetaProps;
  trackingValues?: ClickTrackingValues;
  ctaCopy: string;
  sectionProps?: SectionProps;
  options?: InitOptions;
  callback?: () => void;
}

export const trackClick = (props: ClickTrackingProps) => {
  if (
    props.pageMeta.templateSetting?.enableSegmentTracking &&
    props.trackingValues &&
    props.trackingValues.clickTrachingEnabled
  ) {
    try {
      const event =
        TrackingEventNameMap[
          props.trackingValues.trackingEventName || TrackingEventName.CtaClicked
        ];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const properties: any = {
        title: document.title,
        page_title: document.title,
        referrer: document.referrer,
        path: window.location.pathname,
        url: window.location.href,
        cta_copy: props.ctaCopy,
        cta_type: props.trackingValues.ctaType,
        cta_element: props.trackingValues.ctaElement,
      };

      properties.module =
        props.trackingValues.moduleName ||
        props.sectionProps?.sectionTracking?.moduleViewed ||
        props.sectionProps?.title;
      properties.position_number =
        props.sectionProps?.sectionTracking?.positionNumber ||
        props.sectionProps?.index;

      analytics().track(event, properties);
    } catch (error) {
      // TODO: Implement Error Logging
    }
  }
};
